import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import '../components/AdminLayout.css';
import './Dashboard.css';

const STAT_DEFS = [
  { key: 'totalUsers', label: 'Toplam Kullanıcı', icon: '👥', color: '#EEF2FD', iconColor: '#5784E6' },
  { key: 'totalNotes', label: 'Toplam Not', icon: '📄', color: '#FFF8E6', iconColor: '#FFB400' },
  { key: 'activeNotes', label: 'Aktif Notlar', icon: '✅', color: '#E8F5E9', iconColor: '#4CAF50' },
  { key: 'deletedNotes', label: 'Silinmiş Notlar', icon: '🗑️', color: '#FFEBEE', iconColor: '#E53935' },
  { key: 'totalMatches', label: 'Toplam Eşleşme', icon: '⇄', color: '#EDE7F6', iconColor: '#7B1FA2' },
  { key: 'completedMatches', label: 'Tamamlanan Eşleşme', icon: '☑', color: '#E8F5E9', iconColor: '#388E3C' },
  { key: 'pendingMatches', label: 'Bekleyen Eşleşme', icon: '⏳', color: '#FFF8E6', iconColor: '#F57C00' },
  { key: 'totalMessages', label: 'Toplam Mesaj', icon: '💬', color: '#E3F2FD', iconColor: '#1976D2' },
  { key: 'activeCourses', label: 'Aktif Ders Seçeneği', icon: '📚', color: '#E8EAF6', iconColor: '#3F51B5' },
];

async function safeCount(table, filters = []) {
  try {
    let q = supabase.from(table).select('*', { count: 'exact', head: true });
    for (const [col, val] of filters) q = q.eq(col, val);
    const { count } = await q;
    return count || 0;
  } catch { return 0; }
}

function statusBadge(s) {
  const map = { completed: ['badge-green', 'Tamamlandı'], pending: ['badge-orange', 'Bekliyor'], approved: ['badge-green', 'Uygun'], rejected: ['badge-red', 'Reddedildi'] };
  const [cls, label] = map[s] || ['badge-gray', s || '-'];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('tr-TR') : '-'; }

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [recentNotes, setRecentNotes] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [aiSummary, setAiSummary] = useState({ approved: 0, pending: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      // Counts — individually so one failure doesn't break all
      const [totalUsers, totalNotes, activeNotes, deletedNotes, totalMatches, completedMatches, pendingMatches, totalMessages, activeCourses] = await Promise.all([
        safeCount('profiles'),
        safeCount('notes'),
        safeCount('notes', [['is_deleted', false]]),
        safeCount('notes', [['is_deleted', true]]),
        safeCount('matches'),
        safeCount('matches', [['final_status', 'completed']]),
        safeCount('matches', [['final_status', 'pending']]),
        safeCount('messages'),
        safeCount('course_options', [['is_active', true]]),
      ]);
      setStats({ totalUsers, totalNotes, activeNotes, deletedNotes, totalMatches, completedMatches, pendingMatches, totalMessages, activeCourses });

      // AI summary
      const [aiA, aiP, aiR] = await Promise.all([
        safeCount('notes', [['ai_status', 'approved']]),
        safeCount('notes', [['ai_status', 'pending']]),
        safeCount('notes', [['ai_status', 'rejected']]),
      ]);
      setAiSummary({ approved: aiA, pending: aiP, rejected: aiR });

      // Recent notes
      const { data: notes } = await supabase.from('notes').select('id, title, created_at, is_deleted, ai_status, user_id').order('created_at', { ascending: false }).limit(5);
      if (notes && notes.length > 0) {
        const userIds = [...new Set(notes.map(n => n.user_id).filter(Boolean))];
        let profileMap = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase.from('profiles').select('id, name').in('id', userIds);
          if (profiles) profiles.forEach(p => { profileMap[p.id] = p.name; });
        }
        setRecentNotes(notes.map(n => ({ ...n, ownerName: profileMap[n.user_id] || '-' })));
      }

      // Recent matches
      const { data: matches } = await supabase.from('matches').select('id, final_status, created_at, user_a_id, user_b_id').order('created_at', { ascending: false }).limit(5);
      if (matches && matches.length > 0) {
        const userIds = [...new Set(matches.flatMap(m => [m.user_a_id, m.user_b_id]).filter(Boolean))];
        let profileMap = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase.from('profiles').select('id, name').in('id', userIds);
          if (profiles) profiles.forEach(p => { profileMap[p.id] = p.name; });
        }
        setRecentMatches(matches.map(m => ({ ...m, userAName: profileMap[m.user_a_id] || '-', userBName: profileMap[m.user_b_id] || '-' })));
      }
    } catch (e) {
      console.error('Dashboard error:', e);
    }
    setLoading(false);
  }

  return (
    <div className="dashboard-page">
      <div className="admin-stat-cards">
        {STAT_DEFS.map(def => (
          <div className="admin-stat-card" key={def.key}>
            <div className="stat-card-icon" style={{ background: def.color }}>
              <span style={{ color: def.iconColor }}>{def.icon}</span>
            </div>
            <div className="stat-card-value">
              {loading ? '—' : (stats[def.key] ?? 0).toLocaleString('tr-TR')}
            </div>
            <div className="stat-card-label">{def.label}</div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">Son Yüklenen Notlar</h2>
          </div>
          <div className="admin-table-wrapper">
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr><th>Başlık</th><th>Sahip</th><th>AI Durumu</th><th>Durum</th><th>Tarih</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="admin-loading-row"><td colSpan={5}>Yükleniyor...</td></tr>
                  ) : recentNotes.length === 0 ? (
                    <tr className="admin-loading-row"><td colSpan={5}>Henüz not yok.</td></tr>
                  ) : recentNotes.map(n => (
                    <tr key={n.id}>
                      <td><strong>{n.title || '-'}</strong></td>
                      <td>{n.ownerName}</td>
                      <td>{statusBadge(n.ai_status)}</td>
                      <td>{n.is_deleted ? <span className="badge badge-red">Silinmiş</span> : <span className="badge badge-green">Aktif</span>}</td>
                      <td>{fmtDate(n.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2 className="admin-section-title">Son Eşleşmeler</h2>
          </div>
          <div className="admin-table-wrapper">
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr><th>Kullanıcı A</th><th>Kullanıcı B</th><th>Son Durum</th><th>Tarih</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="admin-loading-row"><td colSpan={4}>Yükleniyor...</td></tr>
                  ) : recentMatches.length === 0 ? (
                    <tr className="admin-loading-row"><td colSpan={4}>Henüz eşleşme yok.</td></tr>
                  ) : recentMatches.map(m => (
                    <tr key={m.id}>
                      <td><strong>{m.userAName}</strong></td>
                      <td>{m.userBName}</td>
                      <td>{statusBadge(m.final_status)}</td>
                      <td>{fmtDate(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="admin-section" style={{ gridColumn: '1 / -1' }}>
          <div className="admin-section-header">
            <h2 className="admin-section-title">AI Kontrol Özeti</h2>
          </div>
          <div className="ai-summary-cards">
            <div className="ai-card ai-card-green">
              <span className="ai-card-value">{loading ? '—' : aiSummary.approved}</span>
              <span className="ai-card-label">Uygun</span>
            </div>
            <div className="ai-card ai-card-orange">
              <span className="ai-card-value">{loading ? '—' : aiSummary.pending}</span>
              <span className="ai-card-label">Beklemede</span>
            </div>
            <div className="ai-card ai-card-red">
              <span className="ai-card-value">{loading ? '—' : aiSummary.rejected}</span>
              <span className="ai-card-label">Reddedildi</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
